import * as d3 from 'd3';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as browser from 'lib/Browser';
import getOpenVersionForClass from 'notebook/notes/util/getOpenVersionForClass';

import {SignedFileQuery, SignedFileQueryVariables} from './__generated/SignedFileQuery';
import {SIGNED_FILE_QUERY} from './SignedExtFileURLProxy';

/**
 * Converts an image from url to base64 representation
 * We can avoid issues with pre-signed s3 urls expiring
 * by saving the base64 to the src tag of an img
 */
export function urlToBase64(signedURL: string): Promise<string> | void {
  if (browser.isBrowser) {
    // eslint-disable-next-line no-async-promise-executor -- Disabling for now, but worth fixing
    return new Promise(async (resolve, reject) => {
      const response = await fetch(signedURL);
      if (!response.ok) {
        reject(response.status);
      }
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(new Blob([blob], {type: 'image/jpeg'}));
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  }
}

/**
 * Perform a Signed File query using ApolloClient with the given apiId and url
 * for a signedUrl. Use useRedirectURL to instead request the redirectUrl, which
 * is used when a shorter url is needed ie. Microsoft Office Viewer has a URL
 * limit but can follow a redirect.
 *
 * @param apiId - Api id of the external file
 * @param url - URL of the external file requested
 * @param owningEntityApiId - Used in the GQL resolver to authenticate that the external file exists with the entity
 * @param versionId - Used in the GQL resolver to find the file within the owning entity's version (if applicable)
 */
export async function performSignedFileQuery(
  apiId: string,
  url: string,
  owningEntityApiId: string,
  versionId?: number | null,
  options?: {addHtmlContentTypeHeader?: boolean; useRedirectURL?: boolean}
): Promise<string | null> {
  if (!versionId) {
    versionId = getOpenVersionForClass(owningEntityApiId);
  }
  const signedNode = await doQuerySafe<SignedFileQuery, SignedFileQueryVariables>(
    {
      query: SIGNED_FILE_QUERY,
    },
    {
      variables: {
        apiId,
        versionId,
        owningEntityApiId,
        url,
        addHtmlContentTypeHeader: options?.addHtmlContentTypeHeader ?? false,
      },
      fetchPolicy: 'no-cache',
    }
  );
  if (isInaccessible(signedNode.signedFile)) {
    return null;
  }
  if (options?.useRedirectURL ?? false) {
    return signedNode.signedFile.redirectUrl;
  } else {
    return signedNode.signedFile.signedUrl;
  }
}

export enum ExternalFlatFileDelimiter {
  TAB = '\t',
  COMMA = ',',
}

/**
 * Parse rows from a tab or comma-delimited flat file.
 *
 * @param fileText Text of the file to parse
 * @param delimiter The allowed delimiter. If undefined, either TSV or CSV will be inferred.
 */
export function parseRows(fileText: string, delimiter?: ExternalFlatFileDelimiter): Array<Array<string>> {
  const inferredDelimiter = delimiter ?? (fileText.includes('\t') ? '\t' : ',');
  return d3.dsvFormat(inferredDelimiter).parseRows(fileText);
}
